import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import Auth from "service/AuthService";
import { useAuth } from "provider/AuthContext";
import Modal from "components/ui/Modal";
import InputField from "components/ui/forms/InputField";
import { REGEX_EMAIL } from "helper/Validation";
import InputSelect from "components/ui/forms/InputSelect";
import { COUNTRIES, CURRENCIES, CURRENCY_OPTIONS } from "config/DATA";
import InputRC from "components/ui/forms/InputRC";
import Logo from "assets/images/logo.jpg";
import { API_URL } from "helper/Constants";
import { REGEX_USERNAME, preventKeys } from "helper/ValidationPatterns";

const RegisterPage = ({ isOpen, onClose, handleLoginClick }) => {
  const [errorMessage, setErrorMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);
  // const [isAcknowledge, setIsAcknowledge] = useState(true);

  const { updateUser } = useAuth();
  const authApi = new Auth(API_URL);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      username: "", // Initialize empty string instead of undefined
      email: "", // Initialize empty string instead of undefined
      password: "", // Initialize empty string instead of undefined
      confirmPassword: "", // Initialize empty string instead of undefined
      country: { label: "Tunisia", value: "TN" },
      currency: CURRENCY_OPTIONS[0],
      acknowledge: false,
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);

    // Format the data to extract values from country and currency objects
    const formattedData = {
      ...data,
      country: data.country?.label || data.country,
      currency: data.currency?.value || data.currency,
      role: "user", // Adding role as required by backend
    };
    console.log(formattedData, "Data");
    // return;
    try {
      const response = await authApi.registerOnlineUser(formattedData);
      if (response.success) {
        setErrorMessage("");
        console.log(response);
        if (response?.message === "User registered successfully") {
          // navigate("/");
          onClose();
        }
      } else {
        setErrorMessage(response.message || "Failed to login. Please try again.");
      }
      if (response.success) {
        const loginResponse = await authApi.loginUser(data);
        console.log(loginResponse, "Login");
        if (loginResponse.success) {
          updateUser(loginResponse.user);
        }
      }
    } catch (error) {
      console.error("Error during login:", error);
      setErrorMessage(error.response?.data.message || "An error occurred during login. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle country and currency change
  const handleCountryChange = (selectedOption) => {
    setValue("country", selectedOption.value);
  };

  const handleCurrencyChange = (selectedOption) => {
    setValue("currency", selectedOption.value);
  };

  // Handle username input to ensure it's always lowercase
  const handleUsernameChange = (e) => {
    const lowercaseValue = e.target.value.toLowerCase();
    setValue("username", lowercaseValue, { shouldValidate: true });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="sm:max-w-[400px] w-full"
      title={
        <>
          <Link to="/" className="h-[37px] max-w-[100px] flex items-center">
            <img src={Logo} alt="logo" />
          </Link>
          <div className="px-4 ml-auto mr-8">
            <button
              onClick={handleLoginClick}
              className="bg-none p-2 uppercase !text-[12px] text-center underline duration-300 text-white leading-[.45rem] rounded-md cursor-pointer"
            >
              SIGN IN
            </button>
          </div>
        </>
      }
    >
      <div className="mt-2">
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="px-4 pb-4 max-h-[calc(100vh-350px)] overflow-y-auto hide-scrollbar">
            <h2 className="text-[15px] text-slate-400 mt-2">New Member?</h2>
            <p className="text-[17px] mb-3 mt-1 uppercase">Register now, it's easy!</p>
            <div className="md:space-y-3 space-y-2">
              <InputField
                type="text"
                name="username"
                label="Username"
                control={control}
                pattern={REGEX_USERNAME}
                onKeyDown={(e) => preventKeys(e, [" "])}
                onChange={handleUsernameChange}
                inputClassName="lowercase"
                {...register("username", {
                  required: "Username is required",
                  minLength: {
                    value: 4,
                    message: "Username must be at least 4 characters long",
                  },
                  maxLength: {
                    value: 16,
                    message: "Username cannot exceed 16 characters",
                  },
                  pattern: {
                    value: REGEX_USERNAME,
                    message: "Username can only contain letters and numbers (no spaces or special characters)",
                  },
                })}
                error={errors.username?.message}
              />
              <InputField
                type="email"
                name="email"
                label="Email"
                control={control}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: REGEX_EMAIL,
                    message: "Please enter a valid email address",
                  },
                })}
                error={errors.email?.message}
              />

              <InputField
                type={"password"}
                name="password"
                label="Password"
                control={control}
                {...register("password", {
                  required: "Password is required",
                  pattern: {
                    value: /^\S+$/,
                    message: "Password cannot contain spaces",
                  },
                })}
                error={errors.password?.message}
              />
              <InputField
                type={"password"}
                name="confirmPassword"
                label="Confirm Password"
                control={control}
                {...register("confirmPassword", {
                  required: "Confirm password is required",
                  pattern: {
                    value: /^\S+$/,
                    message: "Password cannot contain spaces",
                  },
                  validate: (value, formValues) => value === formValues.password || "Passwords do not match",
                })}
                error={errors.confirmPassword?.message}
              />
              <InputSelect
                searchable
                label="Country"
                name="country"
                control={control}
                options={COUNTRIES.filter((c) => c.code === "TN").map((c) => ({
                  label: c.name,
                  value: c.code,
                }))}
                {...register("country", {
                  required: "Country is required",
                })}
                defaultValue={{ name: "Tunisia", code: "TN" }}
                onChange={handleCountryChange}
              />

              <InputSelect
                searchable
                label="Currency"
                name="currency"
                control={control}
                options={CURRENCIES.filter((c) => c.code === "TND").map((c) => ({
                  label: c.name,
                  value: c.code,
                }))}
                {...register("currency", {
                  required: "Currency is required",
                })}
                defaultValue={{ code: "TND", name: "TND" }}
                onChange={handleCurrencyChange}
              />

              <InputRC
                name="acknowledge"
                type="checkbox"
                register={register}
                validation={{
                  required: "You must accept the terms and conditions to register",
                }}
                error={errors.acknowledge?.message}
                label={
                  <div className="text-sm">
                    <label htmlFor="acknowledge" className="terms-before-reg-btn-text">
                      I am over 18 years of age and have accepted the{" "}
                    </label>
                    <span className="text-white">
                      <a className="underline" href="/?helpPageContent=privacy-policy">
                        <span>Privacy Policy</span>
                      </a>
                      <span className="px-1">and the</span>
                      <a className="underline" href="/?helpPageContent=general-terms-and-conditions">
                        <span>General Terms &amp; Conditions</span>
                      </a>
                    </span>
                  </div>
                }
              />

              {errorMessage && <div className="text-red-500 text-sm mb-4">{errorMessage}</div>}
            </div>
          </div>
          <div className="bg-white/5 py-1 px-4 mb-4">
            <button
              type="submit"
              className="bg-[rgb(16,145,33,1)] w-full font-medium px-2 py-2  rounded-[3px] uppercase leading-3 text-sm transition duration-300 bg-fillButton h-9 mt-3 text-primary-dark disabled:opacity-50 disabled:hover:bg-fillButton hover:bg-[rgb(21,190,43,1)]"
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "REGISTER"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RegisterPage;
